import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Headline from '../../ui/Headline';
import Button from '../../ui/Button';
import ButtonsGroup from '../../ui/ButtonsGroup';
import Input from '../../ui/Input';
import TranslatedFieldsRow from '../TranslationsFieldsRow';
import { useValidator } from '../../hooks/formValidator/useValidator';
import { APP_LANGUAGES } from '../../app.config.vars/languages';
import { formatLangCode } from '../../helpers';
import { createFaqCategory, updateFaqCategory } from '../../api/admin/faq';
import { useAppDispatch } from '../../store';
import { setIsModalOpen, setModalContent } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
// Phase 6 D-12 + D-19 + D-21 + UI-SPEC § "Inline edit form":
//
// Inline create / edit form for a FAQ category title across all three
// languages. The current i18n language renders as the primary `Input`; the
// other two languages render via the existing `TranslatedFieldsRow` pattern
// (auto-translate + per-language Input) so authors keep the WYSIWYG pairing
// with rally/task admin forms.
//
// Validation (D-21):
//   * `titleDE`  required (trim, non-empty) + max 255 (Phase 5 SQL column).
//   * `titleEN`  optional; if non-empty, max 255.
//   * `titleCS`  optional; if non-empty, max 255.
//
// Payload (D-19):
//   * `de` always present.
//   * `en`/`cs` semantics differ between CREATE and UPDATE (Phase 5 D-08
//     + Phase 6 D-19, see api/admin/types.ts lines 81–90):
//       - Create mode: omit the key entirely when blank — backend reads
//         absent as "no translation provided".
//       - Update mode, value blank, translation NEVER existed: same as
//         create — omit the key.
//       - Update mode, value blank, translation PREVIOUSLY existed
//         (i.e. `category.translations[lang].available === true` with a
//         non-empty title): send the key as explicit `null` so the
//         server soft-deletes the localized row. Omitting would silently
//         drop the author's intent to clear the translation.
//     Empty strings MUST NOT be sent — call sites strip and convert.
// Phase 5 SQL column constraint on tx_compass_domain_model_faqcategory.title.
// Kept inline so a static grep for `maxLength: 255` finds the binding.
const MAX_TITLE_LEN = 255; // maxLength: 255
const isTitleRequired = (value) => value.trim().length > 0
    ? null
    : { messageTranslationKey: 'admin.faq.error.title_required' };
const titleMaxLength = (value) => value.length <= MAX_TITLE_LEN
    ? null
    : {
        messageTranslationKey: 'admin.faq.error.title_too_long',
        variableToUseInMessage: { maxLength: MAX_TITLE_LEN },
    };
const optionalTitleMaxLength = (value) => 
// Skip max-length check entirely when empty (en/cs are optional).
!value
    ? null
    : value.length <= MAX_TITLE_LEN
        ? null
        : {
            messageTranslationKey: 'admin.faq.error.title_too_long',
            variableToUseInMessage: { maxLength: MAX_TITLE_LEN },
        };
const categoryFormSchema = {
    titleDE: [isTitleRequired, titleMaxLength],
    titleEN: [optionalTitleMaxLength],
    titleCS: [optionalTitleMaxLength],
};
const buildInitialValues = (category) => ({
    de: category?.translations.de?.title ?? '',
    en: category?.translations.en?.title ?? '',
    cs: category?.translations.cs?.title ?? '',
});
const FaqCategoryEditForm = ({ category, onCancel, onSaved }) => {
    const { i18n, t } = useTranslation();
    const dispatch = useAppDispatch();
    const [values, setValues] = useState(() => buildInitialValues(category));
    const [submitStatus, setSubmitStatus] = useState(null);
    const { formErrors, validateAll } = useValidator({
        inputSchema: categoryFormSchema,
    });
    // WR-04: normalize i18n.language to the supported union ('de'|'en'|'cs').
    // formatLangCode strips region tags; the APP_LANGUAGES guard falls back to
    // 'de' for any tag we do not support so the field-name derivation (`title${currentLang.toUpperCase()}`)
    // never produces a key absent from the schema.
    const rawLang = formatLangCode(i18n.language || 'de');
    const currentLang = (APP_LANGUAGES.some((l) => l.code === rawLang)
        ? rawLang
        : 'de');
    const currentLangFieldName = `title${currentLang.toUpperCase()}`;
    const handleCurrentLangChange = (e) => {
        const next = e.currentTarget.value;
        setValues((prev) => ({ ...prev, [currentLang]: next }));
    };
    const handleTranslatedChange = (lang, value) => {
        setValues((prev) => ({ ...prev, [lang]: value }));
    };
    const handleSave = async () => {
        const errors = validateAll({
            inputValues: {
                titleDE: values.de,
                titleEN: values.en,
                titleCS: values.cs,
            },
        });
        if (Object.keys(errors).length > 0) {
            return;
        }
        // D-19: en/cs handling depends on mode.
        //  - Create mode: blank → omit key (never send empty string).
        //  - Update mode + value blank + translation previously existed → send
        //    explicit `null` so the server soft-deletes the localized row.
        //  - Update mode + value blank + translation never existed → omit.
        // See block comment at top of file and api/admin/types.ts:81-90.
        const wasPresent = (lang) => Boolean(category?.translations[lang]?.available && category.translations[lang]?.title);
        const langBlock = (lang) => {
            const v = values[lang].trim();
            if (v)
                return { [lang]: { title: v } };
            if (category && wasPresent(lang))
                return { [lang]: null };
            return {};
        };
        // Typed as CreateFaqCategoryPayload (de mandatory). Since
        // UpdateFaqCategoryPayload = Partial<CreateFaqCategoryPayload>, the same
        // value is also a valid PATCH body — the union narrows cleanly at the
        // callsite below.
        const payload = {
            translations: {
                de: { title: values.de.trim() },
                ...langBlock('en'),
                ...langBlock('cs'),
            },
        };
        setSubmitStatus('pending');
        try {
            const saved = category
                ? await updateFaqCategory(category.uid, payload)
                : await createFaqCategory(payload);
            setSubmitStatus('success');
            onSaved(saved);
        }
        catch {
            // UI-SPEC § State Contract → on error, KEEP the form open with values
            // intact and surface the generic error modal (D-23). IN-01: param-less
            // catch matches sibling forms (FaqItemEditForm, FaqItemsSection) and
            // sidesteps unused-vars lint warnings.
            setSubmitStatus(null);
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
    };
    const currentLangError = formErrors[currentLangFieldName];
    return (_jsxs("div", { className: "faq-edit-form", children: [_jsx(Headline, { title: t(category ? 'admin.faq.edit_category' : 'admin.faq.add_category'), type: "h3" }), _jsx(Input, { id: `faq-category-title-${currentLang}`, label: t('admin.faq.label.category_title'), placeholder: t('admin.faq.placeholder.category_title'), value: values[currentLang], onChange: handleCurrentLangChange, error: !!currentLangError?.length, errorMessage: currentLangError?.length
                    ? t(currentLangError[0].messageTranslationKey, currentLangError[0].variableToUseInMessage)
                    : undefined }), _jsx(TranslatedFieldsRow, { fieldName: "title", values: values, onChange: handleTranslatedChange, formErrors: formErrors, placeholder: t('admin.faq.placeholder.category_title'), originContent: values[currentLang] || '', isDataReady: true }), _jsxs(ButtonsGroup, { children: [_jsx(Button, { label: t('button.save'), onClick: handleSave, disabled: submitStatus === 'pending' || submitStatus === 'success' }), _jsx(Button, { variant: "outlined", label: t('button.cancel'), onClick: onCancel, disabled: submitStatus === 'pending' || submitStatus === 'success' })] })] }));
};
export default FaqCategoryEditForm;
